import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/use-language";
import { useAdmin } from "@/hooks/use-admin";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "sonner";
import {
  getContentEditors,
  type ContentEditorSummary,
} from "@/lib/admin/content-history.functions";
import { feedbackCount } from "@/lib/feedback.functions";
import {
  Plus,
  Search,
  Eye,
  Pencil,
  Trash2,
  BookOpen,
  Video,
  Palette,
  Target,
  FileText,
  Sparkles,
  Filter,
  Lock,
  Star,
  Copy,
  HelpCircle,
  Printer,
  MessageSquare,
} from "lucide-react";
import { coverFor } from "@/assets/worlds";


export const Route = createFileRoute("/_authenticated/app/admin/")({
  component: AdminIndex,
});

type ContentItem = {
  id: string;
  kind: string;
  status: string;
  tier: string;
  age_group: string;
  title_en: string;
  title_ar: string;
  description_en: string | null;
  description_ar: string | null;
  body_en: string | null;
  body_ar: string | null;
  cover_url: string | null;
  media_url: string | null;
  embed_url: string | null;
  hls_url: string | null;
  subtitle_en_url: string | null;
  subtitle_ar_url: string | null;
  allow_download: boolean;
  is_featured: boolean;
  tags: string[];
  views_count: number;
  completions_count: number;
  scheduled_at: string | null;
  published_at: string | null;
  updated_at: string;
  world_id?: string | null;
  slug?: string | null;
};

const KIND_META: Record<string, { icon: React.ReactNode; en: string; ar: string }> = {
  story: { icon: <BookOpen className="size-4" />, en: "Story", ar: "قصة" },
  video: { icon: <Video className="size-4" />, en: "Video", ar: "فيديو" },
  quiz: { icon: <HelpCircle className="size-4" />, en: "Quiz", ar: "اختبار" },
  printable: { icon: <Printer className="size-4" />, en: "Printable", ar: "نشاط مطبوع" },
  activity: { icon: <Sparkles className="size-4" />, en: "Activity", ar: "نشاط" },
  challenge: { icon: <Target className="size-4" />, en: "Challenge", ar: "تحدي" },
  coloring: { icon: <Palette className="size-4" />, en: "Coloring", ar: "تلوين" },
  worksheet: { icon: <FileText className="size-4" />, en: "Worksheet", ar: "ورقة عمل" },
};

// Color-coded tier badges (per plan): 🟢 free / 🟡 family / 🟣 premium
const TIER_STYLES: Record<string, string> = {
  free: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  family: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  premium: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
};

const TIER_LABEL: Record<string, { en: string; ar: string }> = {
  free: { en: "Free", ar: "مجاني" },
  family: { en: "Family", ar: "عائلة" },
  premium: { en: "Premium", ar: "بريميوم" },
};

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-clay/40 text-foreground/60",
  scheduled: "bg-accent/15 text-accent",
  published: "bg-secondary/15 text-secondary",
  archived: "bg-clay/30 text-foreground/40",
};

function AdminIndex() {
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const { isAdmin, loading: roleLoading } = useAdmin();
  const perms = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [newFeedbackCount, setNewFeedbackCount] = useState(0);

  const [editorsById, setEditorsById] = useState<Record<string, ContentEditorSummary>>({});
  const fetchEditors = useServerFn(getContentEditors);
  const fetchFeedbackCount = useServerFn(feedbackCount);


  useEffect(() => {
    if (roleLoading || !isAdmin) return;
    (async () => {
      const { data } = await supabase
        .from("content_items")
        .select("*")
        .order("updated_at", { ascending: false });
      setItems((data as ContentItem[]) ?? []);
      setLoading(false);
      const ids = (data ?? []).map((d) => d.id);
      if (ids.length > 0) {
        try {
          const rows = await fetchEditors({ data: { content_ids: ids } });
          const map: Record<string, ContentEditorSummary> = {};
          for (const r of rows) map[r.content_id] = r;
          setEditorsById(map);
        } catch {
          /* non-fatal */
        }
      }
    })();
  }, [isAdmin, roleLoading, fetchEditors]);

  useEffect(() => {
    if (perms.loading) return;
    if (!perms.canModerate && !perms.canViewUsers) return;
    (async () => {
      try {
        const { count } = await fetchFeedbackCount();
        setNewFeedbackCount(count);
      } catch {
        /* non-fatal */
      }
    })();
  }, [perms.loading, perms.canModerate, perms.canViewUsers, fetchFeedbackCount]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (kindFilter !== "all" && i.kind !== kindFilter) return false;
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (tierFilter !== "all" && i.tier !== tierFilter) return false;
      if (featuredOnly && !i.is_featured) return false;
      if (!q) return true;
      return (
        i.title_en.toLowerCase().includes(q) ||
        i.title_ar.toLowerCase().includes(q) ||
        i.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [items, search, kindFilter, statusFilter, tierFilter, featuredOnly]);

  const stats = useMemo(() => {
    const total = items.length;
    const published = items.filter((i) => i.status === "published").length;
    const drafts = items.filter((i) => i.status === "draft").length;
    const views = items.reduce((s, i) => s + i.views_count, 0);
    return { total, published, drafts, views };
  }, [items]);

  async function handleDelete(id: string) {
    if (!confirm(isAr ? "هل تريد الحذف؟" : "Delete this item?")) return;
    const { error } = await supabase.from("content_items").delete().eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setItems((prev) => prev.filter((i) => i.id !== id));
    toast.success(isAr ? "تم الحذف" : "Deleted");
  }

  async function handleToggleFeatured(item: ContentItem) {
    const next = !item.is_featured;
    // Optimistic update
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_featured: next } : i)));
    const { error } = await supabase
      .from("content_items")
      .update({ is_featured: next })
      .eq("id", item.id);
    if (error) {
      // revert
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_featured: !next } : i)));
      toast.error(error.message);
      return;
    }
    toast.success(
      next
        ? isAr ? "أُضيف للمميّز" : "Added to featured"
        : isAr ? "أُزيل من المميّز" : "Removed from featured",
    );
  }

  async function handleDuplicate(item: ContentItem) {
    const payload = {
      kind: item.kind as "story",
      status: "draft" as const,
      tier: item.tier as "free",
      age_group: item.age_group as "4-6",
      title_en: item.title_en ? `${item.title_en} (copy)` : "",
      title_ar: item.title_ar ? `${item.title_ar} (نسخة)` : "",
      description_en: item.description_en,
      description_ar: item.description_ar,
      body_en: item.body_en,
      body_ar: item.body_ar,
      cover_url: item.cover_url,
      media_url: item.media_url,
      embed_url: item.embed_url,
      hls_url: item.hls_url,
      subtitle_en_url: item.subtitle_en_url,
      subtitle_ar_url: item.subtitle_ar_url,
      allow_download: item.allow_download,
      is_featured: false,
      tags: item.tags,
      created_by: user?.id ?? null,
    };
    const { data, error } = await supabase
      .from("content_items")
      .insert(payload)
      .select("*")
      .single();
    if (error || !data) {
      toast.error(error?.message ?? "Failed");
      return;
    }
    setItems((prev) => [data as ContentItem, ...prev]);
    toast.success(isAr ? "تم النسخ كمسودة" : "Duplicated as draft");
    navigate({ to: "/app/admin/$id", params: { id: (data as ContentItem).id } });
  }

  if (roleLoading || perms.loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="size-10 rounded-full border-4 border-clay border-t-primary animate-spin" />
      </div>
    );
  }

  const canSeeFeedback = perms.canModerate || perms.canViewUsers;
  if (!isAdmin && !canSeeFeedback) {
    return (
      <div dir={dir} className="px-5 py-16 max-w-md mx-auto text-center">
        <div className="size-16 mx-auto rounded-full bg-clay/30 flex items-center justify-center mb-4">
          <Lock className="size-6 text-foreground/60" />
        </div>
        <h1 className="font-display-en text-2xl font-bold mb-2">
          {isAr ? "وصول محظور" : "Admin only"}
        </h1>
        <p className="text-sm text-foreground/60 mb-6">
          {isAr
            ? "هذه الصفحة مخصصة لمسؤولي جزوري فقط."
            : "This area is for Jozori administrators."}
        </p>
        <Link
          to="/app"
          className="inline-block px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm"
        >
          {isAr ? "العودة" : "Go back"}
        </Link>
      </div>
    );
  }

  return (
    <div dir={dir} className="px-5 py-7 max-w-3xl mx-auto pb-12">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6 animate-fade-in">
        <div>
          <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-1">
            {isAdmin
              ? isAr ? "إدارة المحتوى" : "Content Studio"
              : isAr ? "مركز الفريق" : "Staff Hub"}
          </p>
          <h1
            className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl font-bold tracking-tight`}
          >
            {isAdmin
              ? isAr ? "كل قصص جزوري" : "All of Jozori."
              : isAr ? "أدوات الفريق" : "Team tools."}
          </h1>
          <p className="text-sm text-foreground/60 mt-1">
            {isAdmin
              ? isAr
                ? "أنشئ، عدّل، وانشر بدون لمس الكود."
                : "Create, edit, publish — no code required."
              : isAr
                ? "إدارة التعليقات والملاحظات."
                : "Feedback & moderation tools."}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/app/admin/launch"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/40 hover:border-primary text-foreground/70 hover:text-primary font-bold text-xs transition-colors"
              title={isAr ? "جاهزية الإطلاق" : "Launch checklist"}
            >
              🚀 {isAr ? "الإطلاق" : "Launch"}
            </Link>
            <Link
              to="/app/admin/review"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/40 hover:border-primary text-foreground/70 hover:text-primary font-bold text-xs transition-colors"
              title={isAr ? "طابور المراجعة" : "Review queue"}
            >
              <Eye className="size-3.5" />
              {isAr ? "مراجعة" : "Review"}
            </Link>
            {perms.isSuperAdmin && (
              <Link
                to="/app/admin/subscribers"
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/40 hover:border-primary text-foreground/70 hover:text-primary font-bold text-xs transition-colors"
                title={isAr ? "المشتركون والبثّ" : "Subscribers & broadcast"}
              >
                👥 {isAr ? "المشتركون" : "Subscribers"}
              </Link>
            )}
            <Link
              to="/app/admin/team"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-2xl bg-card border-2 border-clay/40 hover:border-primary text-foreground/70 hover:text-primary font-bold text-xs transition-colors"
              title={isAr ? "الفريق" : "Team"}
            >
              <Lock className="size-3.5" />
              {isAr ? "الفريق" : "Team"}
            </Link>

            <button
              onClick={() => navigate({ to: "/app/admin/$id", params: { id: "new" } })}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm shadow-lg hover:scale-105 transition-transform"
            >
              <Plus className="size-4" />
              {isAr ? "جديد" : "New"}
            </button>
          </div>
        )}
      </div>

      {canSeeFeedback && (
        <div className="mb-6 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px flex-1 bg-clay/40" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/50">
              {isAr ? "أدوات الفريق" : "Staff tools"}
            </span>
            <div className="h-px flex-1 bg-clay/40" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/app/admin/feedback"
              className="group relative flex items-center gap-3 rounded-3xl bg-card border-2 border-clay/40 hover:border-primary/60 p-4 transition-colors"
            >
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                <MessageSquare className="size-5" />
              </div>
              <div className="min-w-1 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-sm">
                    {isAr ? "ملاحظات التجربة" : "Beta feedback"}
                  </p>
                  {newFeedbackCount > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold text-destructive-foreground">
                      {newFeedbackCount} {isAr ? "جديد" : "new"}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isAr ? "ما يصلنا من الأهل — ساعدنا نحسّن جذوري." : "What parents are sending us."}
                </p>
              </div>
            </Link>
          </div>
        </div>
      )}

      {isAdmin && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            <StatCard label={isAr ? "الكل" : "Total"} value={stats.total} />
            <StatCard label={isAr ? "منشور" : "Live"} value={stats.published} color="text-secondary" />
            <StatCard label={isAr ? "مسودة" : "Drafts"} value={stats.drafts} color="text-foreground/60" />
            <StatCard label={isAr ? "مشاهدات" : "Views"} value={stats.views} color="text-accent" />
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="size-4 absolute top-1/2 -translate-y-1/2 start-3 text-foreground/40" />
            <input
              type="text"
              placeholder={isAr ? "ابحث بالعنوان أو الوسم..." : "Search by title or tag..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full ps-10 pe-3 py-2.5 rounded-2xl bg-card border-2 border-clay/30 focus:border-primary outline-none text-sm"
            />
          </div>

          {/* Kind filters */}
          <div className="flex gap-2 overflow-x-auto -mx-5 px-5 pb-2 mb-3 snap-x">
            <FilterChip active={kindFilter === "all"} onClick={() => setKindFilter("all")}>
              <Filter className="size-3" />
              {isAr ? "كل الأنواع" : "All types"}
            </FilterChip>
            {Object.entries(KIND_META).map(([k, m]) => (
              <FilterChip key={k} active={kindFilter === k} onClick={() => setKindFilter(k)}>
                {m.icon}
                {isAr ? m.ar : m.en}
              </FilterChip>
            ))}
          </div>

          {/* Tier + featured filters */}
          <div className="flex flex-wrap gap-2 mb-3">
            {["all", "free", "family", "premium"].map((tv) => (
              <button
                key={tv}
                onClick={() => setTierFilter(tv)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                  tierFilter === tv
                    ? tv === "all"
                      ? "bg-foreground text-background border-foreground"
                      : `border-current ${TIER_STYLES[tv] ?? ""}`
                    : "bg-card border-clay/40 hover:border-foreground/40"
                }`}
              >
                {tv === "all"
                  ? isAr ? "كل المستويات" : "All tiers"
                  : isAr ? TIER_LABEL[tv].ar : TIER_LABEL[tv].en}
              </button>
            ))}
            <button
              onClick={() => setFeaturedOnly((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                featuredOnly
                  ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500"
                  : "bg-card border-clay/40 hover:border-amber-500/60"
              }`}
            >
              <Star className={`size-3 ${featuredOnly ? "fill-current" : ""}`} />
              {isAr ? "المميّز فقط" : "Featured only"}
            </button>
          </div>

          {/* Status filter */}
          <div className="flex flex-wrap gap-2 mb-5">
            {["all", "draft", "scheduled", "published", "archived"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize border-2 transition-all ${
                  statusFilter === s
                    ? "bg-foreground text-background border-foreground"
                    : "bg-card border-clay/40 hover:border-foreground/40"
                }`}
              >
                {s === "all" ? (isAr ? "كل الحالات" : "all") : s}
              </button>
            ))}
          </div>

          {/* List */}
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-card border-2 border-clay/30 rounded-3xl p-3 flex gap-3 animate-pulse"
                >
                  <div className="size-20 rounded-2xl bg-clay/40 shrink-0" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-3 bg-clay/40 rounded w-1/3" />
                    <div className="h-4 bg-clay/40 rounded w-2/3" />
                    <div className="h-3 bg-clay/30 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-card border-2 border-dashed border-clay/40 rounded-3xl p-10 text-center">
              <Sparkles className="size-8 mx-auto mb-3 text-primary" />
              <p className="text-foreground/60 mb-4">
                {items.length === 0
                  ? isAr
                    ? "ابدأ بإنشاء أول قطعة محتوى"
                    : "Create your first piece of content."
                  : isAr
                    ? "لا نتائج للبحث الحالي"
                    : "No results match your filters."}
              </p>
              {items.length === 0 && (
                <button
                  onClick={() => navigate({ to: "/app/admin/$id", params: { id: "new" } })}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm"
                >
                  <Plus className="size-4" />
                  {isAr ? "أضف محتوى" : "Add content"}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((item, idx) => {
                const meta = KIND_META[item.kind] ?? KIND_META.story;
                return (
                  <div
                    key={item.id}
                    className="bg-card border-2 border-clay/30 rounded-3xl p-3 flex gap-3 animate-fade-in hover:border-primary/50 transition-colors"
                    style={{ animationDelay: `${idx * 30}ms`, animationFillMode: "backwards" }}
                  >
                    {/* Cover */}
                    <div className="relative size-20 rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 to-accent/20 shrink-0 flex items-center justify-center">
                      {(() => {
                        const src = coverFor({
                          cover_url: item.cover_url,
                          world_id: item.world_id,
                          age_group: item.age_group,
                          slug: item.slug,
                        });
                        return src ? (
                          <img
                            src={src}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-2xl text-foreground/40">{meta.icon}</span>
                        );
                      })()}
                      {item.is_featured && (
                        <span className="absolute top-1 start-1 bg-amber-500 text-white rounded-full p-0.5 shadow">
                          <Star className="size-3 fill-current" />
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[item.status]}`}
                        >
                          {item.status}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${TIER_STYLES[item.tier]}`}
                        >
                          {item.tier}
                        </span>
                        <span className="text-[10px] text-foreground/50 font-bold">
                          {item.age_group}
                        </span>
                      </div>
                      <p className="font-bold text-sm truncate">
                        {isAr ? item.title_ar || item.title_en : item.title_en || item.title_ar}
                      </p>
                      <p className="text-[11px] text-foreground/50 flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1">
                          {meta.icon}
                          {isAr ? meta.ar : meta.en}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Eye className="size-3" />
                          {item.views_count}
                        </span>
                      </p>
                      {editorsById[item.id] && (
                        <p className="text-[10px] text-foreground/40 truncate mt-0.5" title={new Date(editorsById[item.id].last_at).toLocaleString()}>
                          {isAr ? "آخر تعديل" : "Last edit"}: {editorsById[item.id].last_actor_email ?? "—"} ·{" "}
                          {new Date(editorsById[item.id].last_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button
                        onClick={() => handleToggleFeatured(item)}
                        className={`size-8 rounded-xl flex items-center justify-center transition-colors ${
                          item.is_featured
                            ? "bg-amber-500/20 text-amber-600 hover:bg-amber-500/30"
                            : "bg-clay/30 text-foreground/50 hover:bg-amber-500/15 hover:text-amber-600"
                        }`}
                        aria-label={item.is_featured ? "Unfeature" : "Feature"}
                        title={isAr ? "مميّز" : "Featured"}
                      >
                        <Star className={`size-4 ${item.is_featured ? "fill-current" : ""}`} />
                      </button>
                      <button
                        onClick={() =>
                          navigate({ to: "/app/admin/$id", params: { id: item.id } })
                        }
                        className="size-8 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center transition-colors"
                        aria-label="Edit"
                        title={isAr ? "تعديل" : "Edit"}
                      >
                        <Pencil className="size-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(item)}
                        className="size-8 rounded-xl bg-secondary/10 hover:bg-secondary/20 text-secondary flex items-center justify-center transition-colors"
                        aria-label="Duplicate"
                        title={isAr ? "نسخ" : "Duplicate"}
                      >
                        <Copy className="size-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="size-8 rounded-xl bg-destructive/10 hover:bg-destructive/20 text-destructive flex items-center justify-center transition-colors"
                        aria-label="Delete"
                        title={isAr ? "حذف" : "Delete"}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  color = "text-primary",
}: {
  label: string;
  value: number;
  color?: string;
}) {
  return (
    <div className="bg-card border-2 border-clay/30 rounded-2xl p-2.5 text-center">
      <p className={`font-display-en text-lg font-black ${color}`}>{value}</p>
      <p className="text-[9px] uppercase tracking-widest font-bold text-foreground/60">
        {label}
      </p>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`snap-start shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card border-clay/40 hover:border-primary"
      }`}
    >
      {children}
    </button>
  );
}
